import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { Modal, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Path } from "react-native-svg";

import { StudentPortalShell } from "../../../../../components/student/StudentPortalShell";
import { BrandBackdrop } from "../../../../../components/student/BrandBackdrop";
import { Skeleton } from "../../../../../components/ui";
import {
  getEligibilityIssues,
  getMyRegistration,
  registerForCompetition,
} from "../../../../../lib/registrations";
import { getCompetition } from "../../../../../lib/competitions";
import { getMyStudent } from "../../../../../lib/students";
import { useStudentPortalShellData } from "../../../../../lib/useStudentPortalShellData";
import { formatDateWithYear, formatDeadlineCompact } from "../../../../../lib/datetime";
import { competitionTier, competitionTierLabel } from "../../../../../lib/studentCases";
import {
  evaluatePositionEligibility,
  listPositionsForCompetitionAsStudent,
  upsertStudentPositionInterests,
  type CompetitionPosition,
  type PositionEligibility,
} from "../../../../../lib/positions";
import type { CompetitionDetail, Registration, Student } from "../../../../../types/database";


/**
 * v2 Find Competition — step 3: pre-join detail page.
 *
 * Shows the public face of the competition (title, sponsor, prize,
 * roster fill, dates, brief) plus a Join Program button. Team chat,
 * deliverables, and announcements are NOT shown here — those only
 * unlock once the student joins.
 *
 * After Join, redirects to /student/competition/[id] (the post-join
 * workspace).
 */
export default function FindCompetitionDetailScreen() {
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const id = Array.isArray(params.id) ? params.id[0] : params.id;
  const shell = useStudentPortalShellData();

  const [competition, setCompetition] = useState<CompetitionDetail | null | undefined>(undefined);
  const [student, setStudent] = useState<Student | null>(null);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [joining, setJoining] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Career-interests step state. We collect linked positions on click —
  // not on initial page load — so the discover-step page render isn't
  // blocked on a query that's only relevant after the student commits.
  const [interestsOpen, setInterestsOpen] = useState(false);
  const [linkedPositions, setLinkedPositions] = useState<CompetitionPosition[]>([]);

  const reload = useCallback(async () => {
    if (!id) return;
    const [c, s, r] = await Promise.all([
      getCompetition(id),
      getMyStudent(),
      getMyRegistration(id),
    ]);
    setCompetition(c);
    setStudent(s as Student | null);
    setRegistration(r);
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Final commit: persist registration + opt-ins + redirect. Used by both
  // the no-positions fast path and the modal's confirm button.
  const finalizeRegistration = useCallback(
    async (
      compId: string,
      optIns: { positionId: string; optedIn: boolean }[]
    ): Promise<{ ok: boolean }> => {
      const { error } = await registerForCompetition(compId);
      if (error) {
        setErrorMsg(error.message || "Could not register. Try again.");
        setJoining(false);
        return { ok: false };
      }
      if (optIns.length > 0) {
        // Persist opt-ins post-registration. RLS on the interests table
        // requires student_id = auth.uid() AND a registration relationship
        // (via the position read path), so order matters — register first.
        const { error: spiErr } = await upsertStudentPositionInterests(
          optIns.map((o) => ({
            positionId: o.positionId,
            competitionId: compId,
            optedIn: o.optedIn,
          }))
        );
        if (spiErr) {
          // Don't roll back the registration — the student is registered;
          // they can still manage interests later from the dashboard.
          setErrorMsg("Registered, but couldn't save your role interests: " + spiErr.message);
        }
      }
      router.replace(`/student/competition/${compId}` as Href);
      return { ok: true };
    },
    []
  );

  const onJoin = useCallback(async () => {
    if (!id) return;
    setJoining(true);
    setErrorMsg(null);
    try {
      // Are there linked positions? If so, surface the career-interests
      // step before committing. If not, complete registration silently.
      const positions = await listPositionsForCompetitionAsStudent(id);
      if (positions.length === 0) {
        await finalizeRegistration(id, []);
        return;
      }
      setLinkedPositions(positions);
      setInterestsOpen(true);
      setJoining(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Could not register. Try again.");
      setJoining(false);
    }
  }, [id, finalizeRegistration]);

  if (competition === undefined) {
    return (
      <StudentPortalShell
        topbarTitle="Loading\u2026"
        topbarFirstName={shell.topbarFirstName}
        avatarName={shell.avatarName}
        avatarUrl={shell.avatarUrl}
        identity={shell.identity}
        casesBadgeCount={shell.casesBadgeCount}
        hideSidebar
      >
        <View className="flex-1 bg-sand">
          <View className="max-w-[840px] px-5 py-8 md:px-11">
            <Skeleton width={120} height={11} />
            <Skeleton width={"70%" as unknown as number} height={32} style={{ marginTop: 16 }} />
            <Skeleton width={"50%" as unknown as number} height={14} style={{ marginTop: 14 }} />
            <Skeleton width={"100%" as unknown as number} height={120} style={{ marginTop: 24, borderRadius: 6 }} />
          </View>
        </View>
      </StudentPortalShell>
    );
  }

  if (!competition) {
    return (
      <StudentPortalShell
        topbarTitle="Competition not found"
        topbarFirstName={shell.topbarFirstName}
        avatarName={shell.avatarName}
        avatarUrl={shell.avatarUrl}
        identity={shell.identity}
        casesBadgeCount={shell.casesBadgeCount}
        hideSidebar
      >
        <View className="flex-1 bg-sand">
          <View className="max-w-[840px] px-5 py-8 md:px-11">
            <BackButton />
            <Text
              style={{
                marginTop: 24,
                fontFamily: FONTS.displayMedium,
                fontSize: 28,
                color: COLORS.FOREST.hex,
              }}
            >
              We couldn\'t find that competition.
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.bodyLight,
                fontSize: 14,
                color: STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              It may have closed or been removed. Browse the listing for live programs.
            </Text>
          </View>
        </View>
      </StudentPortalShell>
    );
  }

  const tier = competitionTier(competition);
  const tierLabel = competitionTierLabel(tier);

  const eligibilityIssues = getEligibilityIssues(student, competition);
  const isEligible = eligibilityIssues.length === 0;
  const alreadyRegistered = registration?.status === "registered" ||
    registration?.status === "submitted";

  return (
    <StudentPortalShell
      topbarTitle={competition.name}
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView
        className="flex-1 bg-sand"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 64 }}
      >
        <BrandBackdrop tone="sand" />

        {/* Edge-to-edge dark hero — mirrors the post-join workspace's
            CompactWorkspaceHero so the visual handoff between Discover →
            program detail → workspace feels continuous. */}
        <FindProgramHero
          companyName={competition.company_name}
          programName={competition.name}
          tierLabel={tierLabel}
          opensAt={competition.opens_at}
          closesAt={competition.closes_at}
          teamSizeMin={competition.team_size_min}
          teamSizeMax={competition.max_team_size}
        />

        <View className="max-w-[860px] px-5 py-8 md:px-11" style={{ position: "relative", zIndex: 1 }}>
          <BackButton />

          <View style={{ marginTop: 18, marginBottom: 28, display: "none" }}>
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                color: COLORS.AMBER.hex,
                marginBottom: 6,
              }}
              numberOfLines={1}
            >
              {competition.company_name}  ·  {tierLabel}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.displayMedium,
                fontSize: 38,
                lineHeight: 42,
                color: COLORS.FOREST.hex,
              }}
            >
              {competition.name}
            </Text>
          </View>

          {/* Legacy quick-facts strip — facts surface in the dark hero
              now. Wrapped in `display: none` so the JSX stays intact in
              git history but doesn't render. */}
          <View
            style={{
              display: "none",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <FactBlock label="Closes" value={competition.closes_at ? formatDateWithYear(competition.closes_at) : "TBD"} />
            <FactBlock label="Opens" value={competition.opens_at ? formatDateWithYear(competition.opens_at) : "Now"} />
            <FactBlock
              label="Team"
              value={
                competition.team_size_min === competition.max_team_size
                  ? `${competition.max_team_size}`
                  : `${competition.team_size_min}\u2013${competition.max_team_size}`
              }
            />
            {competition.max_enrollment != null ? (
              <FactBlock label="Cap" value={`${competition.max_enrollment} students`} />
            ) : null}
          </View>

          {/* Brief — editorial treatment matching the workspace info tab. */}
          {competition.brief ? (
            <View style={{ marginBottom: 22 }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: COLORS.AMBER.hex,
                  marginBottom: 4,
                }}
              >
                The brief
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.display,
                  fontSize: 28,
                  lineHeight: 32,
                  letterSpacing: -0.2,
                  color: COLORS.FOREST.hex,
                  marginBottom: 14,
                }}
              >
                What you&apos;re{" "}
                <Text
                  style={{
                    fontFamily: FONTS.display,
                    fontStyle: "italic",
                    color: COLORS.AMBER.hex,
                  }}
                >
                  solving.
                </Text>
              </Text>
              <View
                style={[
                  {
                    backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: STUDENT_THEME.border,
                    padding: 22,
                  },
                  Platform.OS === "web"
                    ? ({
                        boxShadow:
                          "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
                      } as object)
                    : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 14.5,
                    lineHeight: 25,
                    color: COLORS.FOREST.hex,
                  }}
                >
                  {competition.brief}
                </Text>
              </View>
            </View>
          ) : null}

          {/* Eligibility callout (if any) */}
          {!isEligible && eligibilityIssues.length > 0 ? (
            <View
              style={{
                backgroundColor: "rgba(192,57,43,0.04)",
                borderWidth: 1,
                borderColor: "rgba(192,57,43,0.28)",
                borderRadius: 6,
                padding: 18,
                marginBottom: 22,
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: STUDENT_THEME.destructive,
                  marginBottom: 8,
                }}
              >
                You aren\'t eligible for this program
              </Text>
              {eligibilityIssues.slice(0, 3).map((iss, i) => (
                <Text
                  key={i}
                  style={{
                    marginTop: i === 0 ? 0 : 6,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    lineHeight: 20,
                    color: "rgba(28,43,26,0.85)",
                  }}
                >
                  · {iss.reason}
                </Text>
              ))}
            </View>
          ) : null}

          {/* Join CTA */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {alreadyRegistered ? (
              <Pressable
                onPress={() => router.replace(`/student/competition/${competition.id}` as Href)}
                style={[
                  {
                    backgroundColor: COLORS.AMBER.hex,
                    paddingVertical: 14,
                    paddingHorizontal: 26,
                    borderRadius: 4,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: COLORS.BLACK.hex,
                  }}
                >
                  Open program
                </Text>
                <Svg width={11} height={11} viewBox="0 0 10 10" fill="none">
                  <Path
                    d="M2 5h6M5.5 2.5L8 5l-2.5 2.5"
                    stroke={COLORS.BLACK.hex}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              </Pressable>
            ) : (
              <Pressable
                onPress={onJoin}
                disabled={!isEligible || joining}
                style={[
                  {
                    backgroundColor: !isEligible || joining ? "rgba(28,43,26,0.18)" : COLORS.AMBER.hex,
                    paddingVertical: 14,
                    paddingHorizontal: 26,
                    borderRadius: 4,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  },
                  Platform.OS === "web" && isEligible && !joining
                    ? ({ cursor: "pointer" } as unknown as ViewStyle)
                    : Platform.OS === "web"
                      ? ({ cursor: "not-allowed" } as unknown as ViewStyle)
                      : {},
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: !isEligible || joining ? "rgba(255,255,255,0.7)" : COLORS.BLACK.hex,
                  }}
                >
                  {joining ? "Registering\u2026" : "Register"}
                </Text>
              </Pressable>
            )}
            {competition.closes_at ? (
              <Text
                style={{
                  fontFamily: FONTS.bodyMedium,
                  fontSize: 11.5,
                  color: STUDENT_THEME.textOnSurfaceFaint,
                }}
              >
                Registration closes {formatDeadlineCompact(competition.closes_at)}
              </Text>
            ) : null}
          </View>

          {errorMsg ? (
            <Text
              style={{
                marginTop: 12,
                fontFamily: FONTS.bodyMedium,
                fontSize: 12,
                color: STUDENT_THEME.destructive,
              }}
            >
              {errorMsg}
            </Text>
          ) : null}

          {/* What\'s gated until you join */}
          <View
            style={{
              marginTop: 28,
              backgroundColor: STUDENT_THEME.textOnSidebarDim,
              borderWidth: 1,
              borderColor: "rgba(28,43,26,0.07)",
              borderRadius: 6,
              padding: 18,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                color: STUDENT_THEME.textOnSurfaceDim,
                marginBottom: 10,
              }}
            >
              After you join
            </Text>
            <Text
              style={{
                fontFamily: FONTS.bodyLight,
                fontSize: 13,
                lineHeight: 20,
                color: STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              You\'ll get access to the program workspace — team chat, find/form a team, deliverables with templates, program announcements, and submission upload.
            </Text>
          </View>
        </View>
      </ScrollView>
      {interestsOpen && competition ? (
        <CareerInterestsModal
          companyName={competition.company_name}
          positions={linkedPositions}
          student={student}
          onCancel={() => {
            setInterestsOpen(false);
            setJoining(false);
          }}
          onConfirm={async (selections) => {
            setJoining(true);
            await finalizeRegistration(competition.id, selections);
            setInterestsOpen(false);
          }}
        />
      ) : null}
    </StudentPortalShell>
  );
}

function CareerInterestsModal({
  companyName,
  positions,
  student,
  onCancel,
  onConfirm,
}: {
  companyName: string;
  positions: CompetitionPosition[];
  student: Student | null;
  onCancel: () => void;
  onConfirm: (selections: { positionId: string; optedIn: boolean }[]) => Promise<void>;
}) {
  // Build the eligibility decision per position once. Default-checked for
  // eligible / unknown; disabled-and-unchecked for ineligible.
  const decisions = positions.map((p) => {
    const e: PositionEligibility = evaluatePositionEligibility(p, {
      graduation_year: student?.graduation_year ?? null,
      major: student?.major ?? null,
      university: student?.university ?? null,
      us_citizen: null,
      gpa: null,
    });
    return { position: p, eligibility: e };
  });

  // Only positions the student CAN opt in to start checked. We separately
  // pass the un-checked ineligible set to onConfirm so the existing rows
  // (if any) get withdrawn when they re-visit.
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    for (const d of decisions) {
      if (d.eligibility.state !== "ineligible") out[d.position.id] = true;
    }
    return out;
  });
  const [submitting, setSubmitting] = useState(false);

  function toggle(id: string) {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function confirm() {
    setSubmitting(true);
    const selections: { positionId: string; optedIn: boolean }[] = decisions
      // Skip ineligible — never opted in, no withdrawal needed.
      .filter((d) => d.eligibility.state !== "ineligible")
      .map((d) => ({ positionId: d.position.id, optedIn: !!checked[d.position.id] }));
    await onConfirm(selections);
    setSubmitting(false);
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.55)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 600,
            width: "100%",
            maxHeight: "92%",
            alignSelf: "center",
            backgroundColor: COLORS.OFFW.hex,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: STUDENT_THEME.border,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              paddingHorizontal: 22,
              paddingTop: 18,
              paddingBottom: 14,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(28,43,26,0.07)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: COLORS.AMBER.hex,
              }}
            >
              Career interests
            </Text>
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.displayMedium,
                fontSize: 24,
                color: COLORS.FOREST.hex,
              }}
            >
              Roles {companyName} is hiring for
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 19,
                color: STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              By default, you'll be considered for all of them — uncheck any you don't want to be
              considered for. Companies cannot see who opted in until after judging closes.
            </Text>
          </View>

          <ScrollView contentContainerStyle={{ padding: 22, gap: 12 }}>
            {decisions.map(({ position, eligibility }) => {
              const isIneligible = eligibility.state === "ineligible";
              const isUnknown = eligibility.state === "unknown";
              const isChecked = !!checked[position.id];
              return (
                <Pressable
                  key={position.id}
                  onPress={() => {
                    if (!isIneligible) toggle(position.id);
                  }}
                  style={[
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 14,
                      borderRadius: 6,
                      borderWidth: 1,
                      borderColor: isIneligible
                        ? STUDENT_THEME.border
                        : isChecked
                          ? COLORS.AMBER.hex
                          : "rgba(28,43,26,0.18)",
                      backgroundColor: isIneligible
                        ? "rgba(28,43,26,0.04)"
                        : isChecked
                          ? STUDENT_THEME.accentTintSoft
                          : STUDENT_THEME.surfaceRaisedBg,
                      opacity: isIneligible ? 0.6 : 1,
                    },
                    Platform.OS === "web" && !isIneligible
                      ? ({ cursor: "pointer" } as const)
                      : null,
                  ]}
                >
                  <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
                    <View
                      style={{
                        width: 18,
                        height: 18,
                        marginTop: 2,
                        borderRadius: 3,
                        borderWidth: 1.5,
                        borderColor: isIneligible
                          ? "rgba(28,43,26,0.18)"
                          : isChecked
                            ? COLORS.AMBER.hex
                            : "rgba(28,43,26,0.30)",
                        backgroundColor: isChecked && !isIneligible ? COLORS.AMBER.hex : "transparent",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isChecked && !isIneligible ? (
                        <Text style={{ fontSize: 11, color: COLORS.OFFW.hex, fontFamily: FONTS.bodyBold }}>
                          ✓
                        </Text>
                      ) : null}
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: COLORS.FOREST.hex }}>
                        {position.title}
                      </Text>
                      <Text
                        style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: STUDENT_THEME.textOnSurfaceDim }}
                      >
                        {[position.location, position.comp_band].filter(Boolean).join(" · ") || "—"}
                      </Text>
                      {position.description ? (
                        <Text
                          style={{
                            marginTop: 6,
                            fontFamily: FONTS.body,
                            fontSize: 12,
                            lineHeight: 17,
                            color: STUDENT_THEME.textOnSurfaceDim,
                          }}
                          numberOfLines={3}
                        >
                          {position.description}
                        </Text>
                      ) : null}
                      {isIneligible ? (
                        <Text
                          style={{
                            marginTop: 8,
                            fontFamily: FONTS.bodyMedium,
                            fontSize: 11,
                            color: STUDENT_THEME.destructive,
                          }}
                        >
                          {(eligibility as { reason: string }).reason}
                        </Text>
                      ) : isUnknown ? (
                        <Text
                          style={{
                            marginTop: 8,
                            fontFamily: FONTS.body,
                            fontSize: 11,
                            fontStyle: "italic",
                            color: STUDENT_THEME.textOnSurfaceDim,
                          }}
                        >
                          We can't fully check eligibility — your profile is missing{" "}
                          {(eligibility as { missingFields: string[] }).missingFields.join(", ")}.{" "}
                          <Text
                            onPress={() => router.push("/student/profile" as Href)}
                            style={{ color: COLORS.AMBER.hex, textDecorationLine: "underline" }}
                          >
                            Update your profile →
                          </Text>
                        </Text>
                      ) : null}
                      {position.us_citizen_required ? (
                        <Text
                          style={{
                            marginTop: 8,
                            fontFamily: FONTS.body,
                            fontSize: 11,
                            color: STUDENT_THEME.textOnSurfaceDim,
                          }}
                        >
                          By opting in, you confirm you're authorized to work in the US (citizen / permanent resident).
                        </Text>
                      ) : null}
                    </View>
                  </View>
                </Pressable>
              );
            })}
          </ScrollView>

          <View
            style={{
              paddingHorizontal: 22,
              paddingTop: 14,
              paddingBottom: 18,
              borderTopWidth: 1,
              borderTopColor: "rgba(28,43,26,0.07)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 11.5,
                lineHeight: 17,
                color: STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              By submitting, you consent to {companyName} reviewing your competition performance for
              the roles you're opted into. You can withdraw interest at any time from your
              dashboard.
            </Text>
            <View style={{ marginTop: 12, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
              <Pressable
                onPress={onCancel}
                style={[
                  { paddingHorizontal: 14, paddingVertical: 10 },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: STUDENT_THEME.textOnSurfaceDim }}>
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                onPress={() => void confirm()}
                disabled={submitting}
                style={[
                  {
                    paddingHorizontal: 22,
                    paddingVertical: 12,
                    borderRadius: 4,
                    backgroundColor: COLORS.AMBER.hex,
                    opacity: submitting ? 0.5 : 1,
                  },
                  Platform.OS === "web" && !submitting ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: COLORS.BLACK.hex,
                  }}
                >
                  {submitting ? "Registering…" : "Register & opt in"}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Edge-to-edge dark hero for the pre-join competition detail page.
 * Mirrors `<CompactWorkspaceHero>` (post-join) but doesn't show the case
 * progress donut — there's no team / submissions yet. Instead the right
 * column spotlights the close date as a quiet "registration window"
 * cue so the reader sees the deadline without scanning the chip row.
 */
function FindProgramHero({
  companyName,
  programName,
  tierLabel,
  opensAt,
  closesAt,
  teamSizeMin,
  teamSizeMax,
}: {
  companyName: string;
  programName: string;
  tierLabel: string;
  opensAt: string | null;
  closesAt: string | null;
  teamSizeMin: number;
  teamSizeMax: number;
}) {
  const isWeb = Platform.OS === "web";
  const yearFromIso = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return String(d.getFullYear());
  };
  const year = yearFromIso(opensAt) ?? yearFromIso(closesAt);
  const kicker = year
    ? `${companyName.toUpperCase()} · ${tierLabel.toUpperCase()} · ${year}`
    : `${companyName.toUpperCase()} · ${tierLabel.toUpperCase()}`;
  const teamSizeLabel =
    teamSizeMin === teamSizeMax
      ? `Teams of ${teamSizeMax}`
      : `Teams of ${teamSizeMin}–${teamSizeMax}`;
  const closesLabel = closesAt
    ? `Closes ${new Date(closesAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })}`
    : "Closing date TBD";
  const opensLabel = opensAt
    ? `Opens ${new Date(opensAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })}`
    : null;

  return (
    <View
      style={[
        {
          backgroundColor: COLORS.FOREST.hex,
          paddingVertical: 28,
          paddingHorizontal: 36,
          flexDirection: "row",
          alignItems: "center",
          gap: 32,
          flexWrap: "wrap",
          overflow: "hidden",
          position: "relative",
          borderBottomWidth: 1,
          borderBottomColor: STUDENT_THEME.accentHalo,
        },
        isWeb
          ? ({
              backgroundImage:
                "radial-gradient(ellipse 800px 420px at 80% 30%, rgba(181,135,58,0.10) 0%, transparent 60%)",
            } as object)
          : null,
      ]}
    >
      <View style={{ flex: 1, minWidth: 280, gap: 8 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <View style={{ width: 28, height: 1, backgroundColor: COLORS.AMBER.hex }} />
          <Text
            numberOfLines={1}
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 2.2,
              color: COLORS.AMBER.hex,
            }}
          >
            {kicker}
          </Text>
        </View>

        <Text
          numberOfLines={2}
          style={{
            fontFamily: FONTS.display,
            fontSize: 38,
            lineHeight: 42,
            letterSpacing: -0.3,
            color: COLORS.LINEN.hex,
          }}
        >
          {programName}
        </Text>

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 16,
            marginTop: 4,
          }}
        >
          <HeroChip label="Registration open" tone="sage" />
          <HeroMeta label={teamSizeLabel} />
          <HeroMeta label={closesLabel} />
        </View>
      </View>

      {opensLabel ? (
        <View style={{ minWidth: 160, alignItems: "flex-end", flexShrink: 0 }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              color: COLORS.AMBER.hex,
              textTransform: "uppercase",
            }}
          >
            Window
          </Text>
          <Text
            style={{
              marginTop: 6,
              fontFamily: FONTS.displayMedium,
              fontSize: 18,
              color: COLORS.LINEN.hex,
            }}
          >
            {opensLabel}
          </Text>
          {closesLabel ? (
            <Text
              style={{
                marginTop: 2,
                fontFamily: FONTS.body,
                fontSize: 12,
                color: "rgba(247,244,238,0.65)",
              }}
            >
              {closesLabel}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function HeroChip({ label, tone }: { label: string; tone: "sage" | "amber" }) {
  const config =
    tone === "sage"
      ? {
          color: COLORS.SAGE.hex,
          bg: STUDENT_THEME.successTintSoft,
          border: STUDENT_THEME.successBorderSoft,
        }
      : {
          color: COLORS.AMBER.hex,
          bg: STUDENT_THEME.accentSoft,
          border: STUDENT_THEME.accentBorderHover,
        };
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 11,
        paddingVertical: 4,
        borderRadius: 999,
        borderWidth: 1,
        backgroundColor: config.bg,
        borderColor: config.border,
      }}
    >
      <View style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: config.color }} />
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.5,
          color: config.color,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function HeroMeta({ label }: { label: string }) {
  return (
    <Text
      style={{
        fontFamily: FONTS.body,
        fontSize: 12,
        color: STUDENT_THEME.textOnSidebarDim,
      }}
    >
      {label}
    </Text>
  );
}

function FactBlock({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 140,
        backgroundColor: STUDENT_THEME.surfaceRaisedBg,
        borderWidth: 1,
        borderColor: COLORS.SAND_DARK.hex,
        borderRadius: 4,
        paddingVertical: 12,
        paddingHorizontal: 16,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 8.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: STUDENT_THEME.textOnSurfaceDim,
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 18,
          color: COLORS.FOREST.hex,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function BackButton() {
  return (
    <Pressable
      onPress={() => router.back()}
      accessibilityRole="button"
      accessibilityLabel="Back"
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          paddingVertical: 4,
        },
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
      ]}
    >
      <Svg width={11} height={11} viewBox="0 0 10 10" fill="none">
        <Path
          d="M8 5H2M4.5 2.5L2 5l2.5 2.5"
          stroke={COLORS.AMBER.hex}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
      <Text
        style={{
          fontFamily: FONTS.bodySemiBold,
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: COLORS.AMBER.hex,
        }}
      >
        Back
      </Text>
    </Pressable>
  );
}
